import { fullVersion, minute } from "@pentacode/core";
import type { LitElement } from "lit";
import { Workbox } from "workbox-window";
import "../elements/update-notification";
import type { UpdateNotification } from "../elements/update-notification";
import { DEV_PTC_ENABLE_SW, IS_DEV } from "../env";
import { appMode } from "../init";
import { getSingleton } from "../lib/singleton";

type Constructor<T> = new (...args: any[]) => T;

/**
 * This class is responsible for registering the service worker (sw.ts) and handling updates
 * to both the service worker itself, as well as the application.
 *
 * The app version and service worker version can be different because as soon as a new
 * service worker is available it is installed, but the app is only update on a page reload,
 * which can be triggered by the update dialog, but also be dismissed.
 *
 * This class repeatedly checks for service worker updates, and once it installed a new service worker version,
 * checks for the new app.
 *
 * After that, it handles 'outdated-client' events and specifically checks for app updates.
 */
export function ServiceWorker<B extends Constructor<LitElement>>(baseClass: B) {
    return class extends baseClass {
        private _wb?: Workbox;

        constructor(...args: any[]) {
            super(...args);
            void this._initSW();
        }

        private async _initSW() {
            // An embedded app must not register a service worker against the
            // partner host page's scope (PTC-3919); the integration build
            // ships without one anyway.
            if (appMode === "embedded") {
                return;
            }

            // Disable SW in dev mode unless explicitly enabled
            if (IS_DEV && !DEV_PTC_ENABLE_SW) {
                console.log("[SW] Disabled in dev mode. Set DEV_PTC_ENABLE_SW=true to enable.");

                // unregister any existing service worker
                if ("serviceWorker" in navigator) {
                    const registrations = await navigator.serviceWorker.getRegistrations();
                    for (const registration of registrations) {
                        if (registration.active?.scriptURL.endsWith("/sw.js")) {
                            console.log("[SW] Unregistering existing service worker:", registration);
                            // eslint-disable-next-line no-await-in-loop
                            await registration.unregister();
                        }
                    }
                }

                return;
            }

            const isBot = /bot|crawl|spider|google-read-aloud|googlebot|slurp/i.test(navigator.userAgent);
            if (!("serviceWorker" in navigator) || isBot) {
                return;
            }

            // vite-plugin-pwa serves the SW at `/dev-sw.js?dev-sw` (as an ES
            // module) in dev, and at `/sw.js` (classic) in prod builds.
            this._wb = IS_DEV ? new Workbox("/dev-sw.js?dev-sw", { type: "module" }) : new Workbox("/sw.js");

            // "waiting" means there is a new service worker waiting that is not installed yet
            this._wb.addEventListener("waiting", async (e) => {
                if (e.sw) {
                    await this._onServiceWorkerWaiting();
                }
            });

            /* Outdated client can be fired per request, so we only listen
             * once, check after receiving the event, and register again */
            const listenForOutDatedClientOnce = () =>
                this.addEventListener(
                    "outdated-client",
                    async () => {
                        console.warn("Outdated client detected, checking for update.");
                        await this._checkForAppUpdate();
                        listenForOutDatedClientOnce();
                    },
                    { once: true }
                );
            listenForOutDatedClientOnce();

            await this._wb.register();

            this._pollForUpdates();
        }

        private async _onServiceWorkerWaiting() {
            if (!this._wb) {
                return;
            }

            this._wb.addEventListener("controlling", async () => {
                await this._onServiceWorkerControlling();
            });

            //tell the service worker to skip waiting, i.e. install immediately
            await this._wb.messageSW({ type: "INSTALL_UPDATE" });
        }

        private async _checkForServiceWorkerUpdates() {
            if (!this._wb) {
                return;
            }

            try {
                await this._wb.update();
            } catch (e) {
                console.error("Error checking for updates", e);
            }
        }

        private async _onServiceWorkerControlling() {
            if (!this._wb) {
                return;
            }

            /**
             * We always update when the service worker changed. This is normally the case when we
             * release an update, because then the version in the version info is different.
             */
            void (getSingleton("ptc-update-notification") as UpdateNotification).show();
        }

        /** This checks if there is an app update available */
        private async _checkForAppUpdate() {
            if (!this._wb) {
                return;
            }

            const { version: serviceWorkerVersion } = (await this._wb.messageSW({
                type: "GET_VERSION",
            })) as { version: string };

            /** Specifically for outdated clients, we check if there is an app-update available.
             * In the unlikely case that we get an outdated client event, but we do not actually
             * have a new service worker, we don't want to show the update dialog.
             */
            if (serviceWorkerVersion !== fullVersion) {
                void (getSingleton("ptc-update-notification") as UpdateNotification).show();
            }
        }

        private _pollForUpdates(interval = 10 * minute) {
            void this._checkForServiceWorkerUpdates();
            setInterval(() => {
                void this._checkForServiceWorkerUpdates();
            }, interval);
        }
    };
}
